import React from 'react';
import { T, tintFor, initialsOf } from './login.theme';
import { COPY } from './login.copy';
import { AlertIcon, BackspaceIcon, PrinterIcon, Spinner } from './LoginIcons';
import { authenticate, isWaiter, businessIdOf, authErrorMessage } from './authenticate';
import { touchProfile } from './waiterProfiles';
import { writeTillBusinessId } from './tillStore';

/**
 * The waiter's way in: a four-digit PIN on a numeric pad.
 *
 * The till is a touch screen with no keyboard, so this replaces the pop-up and
 * its text field entirely. It takes over the right-hand rail — same width, same
 * place — rather than floating over the screen in a dialog, because a thumb
 * reaching for a tile should find the pad in the same half of the glass.
 *
 * Four digits and it signs in on its own. There is no confirm key: asking
 * someone to press Enter after a PIN they have already finished typing is a
 * step that exists only for the machine's benefit.
 */

const PIN_LENGTH = 4;

/** 1-9, then Close · 0 · backspace. Phone order, not calculator order. */
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

/* ── one key ──────────────────────────────────────────────────────────── */

function Key({ children, onPress, disabled, tone = 'digit', label }) {
  const [down, setDown] = React.useState(false);

  const palette =
    tone === 'brand'
      ? { bg: down ? T.brandInk : T.brand, border: 'transparent', ink: T.surface }
      : tone === 'quiet'
      ? { bg: down ? T.hover : T.sunk, border: T.border, ink: T.inkSoft }
      : { bg: down ? T.hover : T.surface, border: T.borderStrong, ink: T.ink };

  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onPointerDown={() => setDown(true)}
      onPointerUp={() => setDown(false)}
      onPointerLeave={() => setDown(false)}
      onPointerCancel={() => setDown(false)}
      onClick={onPress}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 74,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: T.radiusPanel,
        color: palette.ink,
        font: 'inherit',
        fontSize: tone === 'brand' ? 15 : 26,
        fontWeight: tone === 'brand' ? 700 : 600,
        fontVariantNumeric: 'tabular-nums',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        // A tap must not select the digit, summon a magnifier, or flash a grey
        // box — all three make a POS feel like a web page.
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
        transform: down ? 'scale(0.97)' : 'none',
        transition: 'transform 90ms ease, background 90ms ease',
      }}
    >
      {children}
    </button>
  );
}

/* ── the pad ──────────────────────────────────────────────────────────── */

export default function WaiterPinPad({ profile, onCancel, onSignedIn, onStaleProfile }) {
  const [pin, setPin] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [stale, setStale] = React.useState(false);
  // Bumped on every failure so the shake replays even for the same message.
  const [shake, setShake] = React.useState(0);

  const tint = tintFor(profile);
  const locked = busy || stale;

  const signIn = React.useCallback(
    async (candidate) => {
      setBusy(true);
      setError('');

      try {
        const { user, token } = await authenticate({
          username: profile.username,
          password: candidate,
        });

        // Re-checked on every unlock, never trusted from the tile: a manager
        // may have changed this person's role since it was pinned.
        if (!isWaiter(user)) {
          setPin('');
          setStale(true);
          setBusy(false);
          return;
        }

        const businessId = businessIdOf(user);
        if (businessId) {
          writeTillBusinessId(businessId);
          touchProfile(businessId, user._id || user.id);
        }

        // Deliberately leaves `busy` true — the screen is about to be replaced
        // by the app, and re-enabling the pad first only lets a second tap in.
        onSignedIn({ user, token });
      } catch (err) {
        setPin('');
        setShake((n) => n + 1);
        // The username came off a tile the server already vouched for, so a
        // refusal here means one thing: the PIN was wrong. Anything else
        // (offline, server down) still gets the general message.
        const status = err?.response?.status;
        setError(status === 401 || status === 400 ? COPY.wrongPin : authErrorMessage(err));
        setBusy(false);
      }
    },
    [profile, onSignedIn]
  );

  const press = React.useCallback(
    (digit) => {
      if (locked || pin.length >= PIN_LENGTH) return;
      const next = pin + digit;
      setError('');
      setPin(next);
      if (next.length === PIN_LENGTH) signIn(next);
    },
    [locked, pin, signIn]
  );

  const backspace = React.useCallback(() => {
    if (locked) return;
    setError('');
    setPin((current) => current.slice(0, -1));
  }, [locked]);

  /**
   * The pad is the point, but a till that does have a keyboard plugged in
   * should still work — and it costs four lines.
   */
  React.useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key >= '0' && event.key <= '9') press(event.key);
      else if (event.key === 'Backspace') backspace();
      else if (event.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [press, backspace, onCancel, busy]);

  return (
    <aside
      style={{
        width: T.railWidth,
        flexShrink: 0,
        height: '100%',
        background: T.surface,
        borderLeft: `1px solid ${T.border}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 18,
        padding: '28px 20px 22px',
        boxSizing: 'border-box',
        overflowY: 'auto',
      }}
    >
      {/* ── who ── */}
      <div
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}
      >
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 64,
            height: 64,
            borderRadius: T.radiusPill,
            background: tint.bg,
            fontSize: 21,
            fontWeight: 700,
            color: tint.ink,
          }}
        >
          {initialsOf(profile.name || profile.username)}
        </span>
        <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: -0.2, color: T.ink }}>
          {profile.name || profile.username}
        </span>
      </div>

      {stale ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, width: '100%' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '12px 13px',
              background: T.dangerTint,
              border: `1px solid ${T.dangerBorder}`,
              borderRadius: T.radiusControl,
            }}
          >
            <span style={{ display: 'flex', paddingTop: 1, color: T.danger }}>
              <AlertIcon size={16} />
            </span>
            <span style={{ fontSize: 12.5, color: T.danger, lineHeight: 1.5 }}>
              {COPY.noLongerWaiter}
            </span>
          </div>
          <Key tone="brand" onPress={() => onStaleProfile(profile)} label={COPY.removeThisTile}>
            {COPY.removeThisTile}
          </Key>
          <Key tone="quiet" onPress={onCancel} label={COPY.close}>
            <span style={{ fontSize: 15, fontWeight: 700 }}>{COPY.close}</span>
          </Key>
        </div>
      ) : (
        <React.Fragment>
          {/* ── four dots ── */}
          <div
            key={shake}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              height: 26,
              animation: error ? 'dalabtireShake 0.4s ease' : 'none',
            }}
          >
            {[0, 1, 2, 3].map((slot) => {
              const filled = slot < pin.length;
              return (
                <span
                  key={slot}
                  style={{
                    width: filled ? 18 : 14,
                    height: filled ? 18 : 14,
                    borderRadius: T.radiusPill,
                    background: filled ? (error ? T.danger : T.brand) : 'transparent',
                    border: filled ? 'none' : `2px solid ${T.inkGhost}`,
                    transition: 'width 110ms ease, height 110ms ease, background 110ms ease',
                  }}
                />
              );
            })}
          </div>

          {/* Fixed-height slot: the pad must not jump when a message appears. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
              height: 20,
              textAlign: 'center',
            }}
          >
            {busy ? (
              <React.Fragment>
                <span style={{ display: 'flex', color: T.inkFaint }}>
                  <Spinner size={14} />
                </span>
                <span style={{ fontSize: 12.5, color: T.inkFaint }}>{COPY.checkingPin}</span>
              </React.Fragment>
            ) : error ? (
              <React.Fragment>
                <span style={{ display: 'flex', color: T.danger }}>
                  <AlertIcon size={14} />
                </span>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: T.danger }}>{error}</span>
              </React.Fragment>
            ) : (
              <span style={{ fontSize: 12.5, color: T.inkSoft }}>{COPY.enterPin}</span>
            )}
          </div>

          {/* ── the pad ── */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 12,
              width: '100%',
            }}
          >
            {KEYS.map((digit) => (
              <Key key={digit} onPress={() => press(digit)} disabled={locked} label={digit}>
                {digit}
              </Key>
            ))}

            <Key tone="brand" onPress={onCancel} disabled={busy} label={COPY.close}>
              {COPY.close}
            </Key>

            <Key onPress={() => press('0')} disabled={locked} label="0">
              0
            </Key>

            <Key
              tone="quiet"
              onPress={backspace}
              disabled={locked || pin.length === 0}
              label={COPY.backspace}
            >
              <BackspaceIcon />
            </Key>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              width: '100%',
              padding: '11px 12px',
              background: T.pendingTint,
              border: `1px solid ${T.pendingBorder}`,
              borderRadius: T.radiusControl,
              boxSizing: 'border-box',
              color: T.pendingInk,
            }}
          >
            <span style={{ display: 'flex', paddingTop: 1 }}>
              <PrinterIcon size={14} />
            </span>
            <span style={{ fontSize: 11.5, lineHeight: 1.5 }}>{COPY.waiterSignOutWarning}</span>
          </div>
        </React.Fragment>
      )}
    </aside>
  );
}
